import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  ADD_INTL_REQUEST_NOTE,
  APPROVE_INTL_UNPAID_SHIPMENT,
  DEACTIVATE_INTL_CUSTOMER_ACCOUNT,
  CANCEL_INTL_MCF_ORDER,
  INTL_MCF_ELIGIBLE_MODELS,
  INTL_MCF_RAW_ORDER,
  INTL_EXPORT_COLUMNS,
  INTL_REQUESTS_EXPORT,
  INTL_REQUEST,
  INTL_REQUESTS,
  INTL_REQUEST_NOTES,
  INTL_STRIPE_CUSTOMER,
  RAISE_INTL_INVOICE,
  REFRESH_INTL_INVOICE,
  REFRESH_INTL_FULFILMENT,
  REFRESH_INTL_INVOICES,
  REFRESH_INTL_MCF_ORDER,
  FULFIL_INTL_VIA_CHINA,
  RELEASE_INTL_MCF_ORDER,
  SUBMIT_INTL_MCF_ORDER,
  SUBMIT_INTL_REQUEST,
  UPDATE_INTL_REQUEST,
  INTL_REQUEST_PRICING,
  CREATE_INTL_QUOTE,
  INTL_REQUEST_FREIGHT_QUOTE,
  SUPERSEDE_INTL_QUOTE,
  CANCEL_INTL_QUOTE,
  CONVERT_INTL_QUOTE_TO_INVOICE,
  REFRESH_INTL_QUOTE,
} from '@staff-app/graphql-client';
import type {
  IntlInvoiceRefresh,
  IntlStripeCustomerInfo,
  IntlRequest,
  IntlRequestFilterInput,
  IntlRequestNote,
  IntlRequestPage,
  IntlFreightQuote,
  IntlRequestPricing,
  CreateIntlQuoteInput,
  RaiseIntlInvoiceInput,
  SubmitIntlRequestInput,
  UpdateIntlRequestInput,
} from '@staff-app/shared-types';
import { STRIPE_CUSTOMER_ID_RE } from '@staff-app/shared-types';
import { useDebounced } from '@/features/order-devices/hooks/useOrderDeviceTasks';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Rows per page.
 *
 * Small compared with the demo-coupon ledger's 200, and the reason is the volume case behind the
 * feature rather than a UI preference: roughly four international orders a month across all
 * sizes. A page of 50 is already more than a year of them, and the table carries wide columns
 * (company, destination, contact) that read badly at 200 rows.
 */
export const INTL_PAGE_SIZE = 50;

/**
 * The page sizes the operator may choose (requester, 2026-09-10).
 *
 * The same three the MDM devices table offers, deliberately: two internal tables that page
 * differently teach nothing except that somebody built them on different days.
 */
export const INTL_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export function useIntlRequests(
  filter: IntlRequestFilterInput,
  offset = 0,
  limit: number = INTL_PAGE_SIZE,
) {
  const debouncedSearch = useDebounced((filter.search ?? '').trim(), 300);
  return useQuery<{ intlRequests: IntlRequestPage }>(INTL_REQUESTS, {
    variables: {
      filter: {
        search: debouncedSearch || undefined,
        shipToCountry: filter.shipToCountry || undefined,
        deviceModel: filter.deviceModel || undefined,
        stage: filter.stage || undefined,
        // Was dropped here, so the Invoice status dropdown changed nothing (Paul, 2026-10-29).
        invoiceStatus: filter.invoiceStatus || undefined,
        // `mine` is resolved SERVER-SIDE from the caller's token; there is deliberately no
        // submitter field to send. Sent only when true, so the filter is absent rather than
        // explicitly false when it is off.
        mine: filter.mine ? true : undefined,
      },
      pagination: { offset, limit },
    },
    fetchPolicy: 'cache-and-network',
  });
}

export function useIntlRequest(requestId: number | null) {
  return useQuery<{ intlRequest: IntlRequest }>(INTL_REQUEST, {
    variables: { requestId: requestId ?? 0 },
    skip: !requestId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useIntlRequestNotes(requestId: number | null) {
  return useQuery<{ intlRequestNotes: IntlRequestNote[] }>(INTL_REQUEST_NOTES, {
    variables: { requestId: requestId ?? 0 },
    skip: !requestId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useSubmitIntlRequest() {
  return useMutation<{ submitIntlRequest: IntlRequest }, { input: SubmitIntlRequestInput }>(
    SUBMIT_INTL_REQUEST,
    // The new row belongs at the top of the list the rep is looking at.
    { refetchQueries: ['intlRequests'] },
  );
}

export function useUpdateIntlRequest() {
  return useMutation<{ updateIntlRequest: IntlRequest }, { input: UpdateIntlRequestInput }>(
    UPDATE_INTL_REQUEST,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

export function useAddIntlRequestNote() {
  return useMutation<
    { addIntlRequestNote: IntlRequestNote },
    { requestId: number; text: string }
  >(ADD_INTL_REQUEST_NOTE, { refetchQueries: ['intlRequestNotes'] });
}

/**
 * Resolve a `cus_…` so the intake form can prefill itself.
 *
 * DEBOUNCED AND SKIPPED UNTIL THE ID LOOKS RIGHT. A rep types `cus_ABC123` one character at a
 * time, and firing a Stripe read per keystroke would be nine wasted round trips and nine chances
 * to show a "no such customer" error about an id that is merely half-typed. So the shape gate
 * comes first and the debounce second.
 *
 * `errorPolicy: 'all'` — a lookup that fails is not a broken form. The rep can still type every
 * field by hand, which is exactly what they did before this existed, so the error is surfaced
 * beside the field rather than thrown.
 */
export function useIntlStripeCustomer(stripeCustomerId: string) {
  const debounced = useDebounced(stripeCustomerId.trim(), 400);
  const looksValid = STRIPE_CUSTOMER_ID_RE.test(debounced);
  return useQuery<{ intlStripeCustomer: IntlStripeCustomerInfo }>(INTL_STRIPE_CUSTOMER, {
    variables: { stripeCustomerId: debounced },
    skip: !looksValid,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
}

/**
 * Raise the draft invoice.
 *
 * Refetches the list, because the row's stage moves FILED → INVOICED and the operator is looking
 * at that row. Not `refreshIntlInvoices` — this returns the updated row itself, so there is
 * nothing to go and re-read.
 */
export function useRaiseIntlInvoice() {
  return useMutation<{ raiseIntlInvoice: IntlRequest }, { input: RaiseIntlInvoiceInput }>(
    RAISE_INTL_INVOICE,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

/**
 * WHAT THIS REQUEST WOULD COST — the summary, before anything is raised.
 *
 * `skip` when there is no request yet, so the form can call it unconditionally. Network-only:
 * the catalog can move between a rep filing and a rep deciding, and a cached figure is a figure
 * that might not be the one the document ends up carrying.
 */
export function useIntlRequestPricing(requestId: number | null) {
  return useQuery<{ intlRequestPricing: IntlRequestPricing }, { requestId: number }>(
    INTL_REQUEST_PRICING,
    {
      variables: { requestId: requestId as number },
      skip: requestId == null,
      fetchPolicy: 'network-only',
    },
  );
}

/**
 * WHAT AMAZON WOULD CHARGE to ship it — the cost beside the price.
 *
 * A SECOND query rather than more fields on the first, because this one crosses the network to
 * Amazon: it is slower, and it fails for ordinary reasons (unconfigured environment, half-typed
 * address, stock out at this quantity). The summary card must render its total without waiting
 * for it and without being broken by it, which two queries give for free and one does not.
 *
 * `errorPolicy: 'all'` so even a transport failure resolves rather than throwing into the form —
 * the server side already never errors, and this covers the case where the server is unreachable.
 */
export function useIntlRequestFreightQuote(requestId: number | null) {
  return useQuery<{ intlRequestFreightQuote: IntlFreightQuote }, { requestId: number }>(
    INTL_REQUEST_FREIGHT_QUOTE,
    {
      variables: { requestId: requestId as number },
      skip: requestId == null,
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  );
}

export function useCreateIntlQuote() {
  return useMutation<{ createIntlQuote: IntlRequest }, { input: CreateIntlQuoteInput }>(
    CREATE_INTL_QUOTE,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

export function useSupersedeIntlQuote() {
  return useMutation<{ supersedeIntlQuote: IntlRequest }, { input: CreateIntlQuoteInput }>(
    SUPERSEDE_INTL_QUOTE,
    // The notes too: superseding writes the old quote's figures into them, and a rep who just
    // pressed it is the one most likely to want to see that line land.
    { refetchQueries: ['intlRequests', 'intlRequest', 'intlRequestNotes'] },
  );
}

export function useCancelIntlQuote() {
  return useMutation<{ cancelIntlQuote: IntlRequest }, { requestId: number }>(CANCEL_INTL_QUOTE, {
    refetchQueries: ['intlRequests', 'intlRequest'],
  });
}

/** CONVERT — the one on this path that bills the customer. */
export function useConvertIntlQuote() {
  return useMutation<
    { convertIntlQuoteToInvoice: IntlRequest },
    { input: { requestId: number; poNumber?: string; poDocumentId?: string } }
  >(CONVERT_INTL_QUOTE_TO_INVOICE, { refetchQueries: ['intlRequests', 'intlRequest'] });
}

export function useRefreshIntlQuote() {
  return useMutation<{ refreshIntlQuote: IntlRequest }, { requestId: number }>(
    REFRESH_INTL_QUOTE,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

export function useRefreshIntlInvoice() {
  return useMutation<{ refreshIntlInvoice: IntlRequest }, { requestId: number }>(
    REFRESH_INTL_INVOICE,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

/**
 * Re-read every live invoice from Stripe.
 *
 * Returns counts rather than rows, so the caller has to refetch the list itself — which it does,
 * because a payment that landed is exactly what the operator pressed this to find out.
 */
export function useRefreshIntlInvoices() {
  return useMutation<{ refreshIntlInvoices: IntlInvoiceRefresh }, Record<string, never>>(
    REFRESH_INTL_INVOICES,
    { refetchQueries: ['intlRequests'] },
  );
}

/**
 * The Amazon half of the one Refresh button.
 *
 * `refetchQueries` for the same reason its Stripe sibling has it: the sweep writes `mcf_status`
 * and `mcf_packages` to our rows, and the table renders the stage DERIVED from those — so a
 * sweep that did not refetch would faithfully re-display the answer it just replaced.
 */
export function useRefreshIntlFulfilment() {
  return useMutation<{ refreshIntlFulfilment: IntlInvoiceRefresh }, Record<string, never>>(
    REFRESH_INTL_FULFILMENT,
    { refetchQueries: ['intlRequests'] },
  );
}

/**
 * Country code → the reader's own language name, via `Intl.DisplayNames`.
 *
 * Why not a table of names: we carry 249 CODES and let the platform render them, so there are no
 * 249 English strings to drift. Same choice as the Quote Generator's countries.ts.
 *
 * Falls back to the bare code when the runtime cannot name it — a code with no label is still a
 * usable answer, and an empty cell is not.
 */
export function countryName(code: string): string {
  try {
    const dn = new Intl.DisplayNames(undefined, { type: 'region' });
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

/**
 * ── Amazon MCF ─────────────────────────────────────────────────────────────────────────────────
 *
 * All four refetch the list as well as the row: the stage moves to `Order-created` and the
 * operator is looking at that row in the queue behind the panel.
 *
 * ROLE-GATED SERVER-SIDE to root + fulfillment for the three that move goods. The panel hides
 * the buttons from anyone else, but that is UX — the mutation is what actually refuses.
 */
export function useSubmitIntlMcfOrder() {
  return useMutation<
    { submitIntlMcfOrder: IntlRequest },
    // No `speed`: every order ships Expedited (requester, 2026-09-22), falling back to Standard
    // only when Amazon refuses it. The service reads the SAME preference list the pricing does,
    // which is what keeps what we quoted and what we ship the same speed.
    { requestId: number }
  >(SUBMIT_INTL_MCF_ORDER, { refetchQueries: ['intlRequests', 'intlRequest'] });
}

export function useReleaseIntlMcfOrder() {
  return useMutation<{ releaseIntlMcfOrder: IntlRequest }, { requestId: number }>(
    RELEASE_INTL_MCF_ORDER,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

/**
 * Record that a request shipped from CHINA — the second fulfilment route.
 *
 * Refetches like the Amazon acts do, and for a sharper reason here: this moves the row's STAGE to
 * FULFILLED, which the ledger behind the drawer renders. Without the refetch the list would still
 * show it waiting on a shipment that has already gone.
 */
export function useFulfilIntlViaChina() {
  return useMutation<
    { fulfilIntlViaChina: IntlRequest },
    { requestId: number; input: { trackingNumber: string; carrier?: string } }
  >(FULFIL_INTL_VIA_CHINA, { refetchQueries: ['intlRequests', 'intlRequest'] });
}

export function useCancelIntlMcfOrder() {
  return useMutation<{ cancelIntlMcfOrder: IntlRequest }, { requestId: number }>(
    CANCEL_INTL_MCF_ORDER,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

export function useRefreshIntlMcfOrder() {
  return useMutation<{ refreshIntlMcfOrder: IntlRequest }, { requestId: number }>(
    REFRESH_INTL_MCF_ORDER,
    { refetchQueries: ['intlRequests', 'intlRequest'] },
  );
}

/**
 * May the signed-in user FILE a request?
 *
 * Mirrors the resolver's root + SUPPORT tier on `submitIntlRequest` — and, just as importantly,
 * on `intlMcfEligibleModels`, `intlStripeCustomer` and `intlRequestPricing`, which are the three
 * reads the intake form cannot work without. Those four move together; granting filing without
 * them produces a form that opens, cannot populate its item picker, and then explains the
 * emptiness by blaming the product catalogue. That is not hypothetical — it is what shipped on
 * 2026-09-21 and what this hook exists to keep from recurring.
 *
 * FULFILMENT IS EXCLUDED, which is not an oversight: they ship what has been ordered and do not
 * open orders. `useCanFulfil` and this are deliberately different pairs, not a hierarchy.
 */
export function useCanFile(): boolean {
  // Same snapshot-stability reasoning as `useCanFulfil` below — select the reference, derive after.
  const roles = useAuthStore((s) => s.adminUser?.roles);
  return Boolean(roles?.some((r) => r === 'root' || r === 'support'));
}

/**
 * May the signed-in user dispatch hardware?
 *
 * Mirrors the resolver's root + FULFILLMENT tier on the three MCF acts. Support is deliberately
 * excluded: a rep can see every field and ask Amazon for a status, but shipping a pallet of
 * devices is somebody else's accountability. The server enforces it; this only decides whether
 * to draw a button that would refuse.
 */
export function useCanFulfil(): boolean {
  // Selects the array REFERENCE from the store and defaults afterwards. `?? []` inside the
  // selector returns a fresh array on every call, which zustand's `useSyncExternalStore` sees as
  // a changed snapshot — "The result of getSnapshot should be cached to avoid an infinite loop",
  // and a render loop in the panel rather than only a warning. Same shape as `StaffSidebar`,
  // which selects `adminUser` and derives from it.
  const roles = useAuthStore((s) => s.adminUser?.roles);
  return Boolean(roles?.some((r) => r === 'root' || r === 'fulfillment'));
}

/**
 * Amazon's verbatim answer about a fulfilment order — fetched only when asked for.
 *
 * LAZY, and `network-only`. It is a window onto what Amazon says RIGHT NOW, so a cached copy
 * would be the one thing it must not be: pressing "show me what Amazon said" and being handed a
 * minute-old answer defeats the point of asking.
 *
 * In production the server returns its refusal AS the value — the surface is switched off there
 * (requester, 2026-09-10), so there is nothing to render but the reason.
 */
export function useIntlMcfRawOrder() {
  return useLazyQuery<{ intlMcfRawOrder: string }, { requestId: number }>(INTL_MCF_RAW_ORDER, {
    fetchPolicy: 'network-only',
  });
}

/**
 * Whether this user may see International Requests, and whether the rollout gate is the reason.
 *
 * ASKED BY THE SHELL, so the tab is not drawn for somebody the server would refuse. The gate
 * itself is server-side (`IntlRolloutGuard`) — this is only what stops a support user clicking
 * into a page that answers 403.
 *
 * `visible` DEFAULTS TO FALSE WHILE LOADING and on error, which is the safe direction for a
 * rollout gate: a tab that appears for half a second and vanishes is worse than one that arrives
 * a moment late.
 */
export function useIntlRequestsAccess(): { visible: boolean; loading: boolean } {
  // Read off `staffMe`, which the shell already holds, so the tabs draw with the page instead of
  // a second later behind a query of their own (Paul, 2026-10-29). Absent reads as hidden.
  const adminUser = useAuthStore((s) => s.adminUser);
  const loaded = useAuthStore((s) => s.adminUserLoaded);
  return { visible: adminUser?.intlRequestsVisible === true, loading: !loaded };
}

/** The columns the export offers. From the server, so the dialog holds no copy of the registry. */
export function useIntlExportColumns() {
  return useQuery<{
    intlExportColumns: Array<{ key: string; label: string; default: boolean }>;
  }>(INTL_EXPORT_COLUMNS, { fetchPolicy: 'cache-first', errorPolicy: 'all' });
}

/**
 * Fetch the CSV on demand.
 *
 * LAZY and `network-only`: an export is a point-in-time snapshot somebody is about to hand to
 * someone else, so serving a cached one — from a different range, or from before this morning's
 * orders — would be the one thing it must not do.
 */
export function useIntlRequestsExport() {
  return useLazyQuery<
    { intlRequestsExport: string },
    // `ids` REPLACES the range rather than narrowing it — see IntlExportDialog. The dates are
    // not sent at all when a selection exists, so the two are never both present in practice.
    { input: { from?: string; to?: string; columns?: string[]; ids?: number[] } }
  >(INTL_REQUESTS_EXPORT, { fetchPolicy: 'network-only' });
}

/**
 * ── The two ROOT-ONLY overrides ────────────────────────────────────────────────────────────────
 *
 * Both refetch the list as well as the row: the stage moves and the badges change, and the
 * operator is looking at that row in the queue behind the panel.
 */
export function useApproveIntlUnpaidShipment() {
  return useMutation<
    { approveIntlUnpaidShipment: IntlRequest },
    { requestId: number; reason: string }
  >(APPROVE_INTL_UNPAID_SHIPMENT, { refetchQueries: ['intlRequests', 'intlRequest'] });
}

export function useDeactivateIntlCustomerAccount() {
  return useMutation<
    { deactivateIntlCustomerAccount: IntlRequest },
    { requestId: number; reason: string }
  >(DEACTIVATE_INTL_CUSTOMER_ACCOUNT, { refetchQueries: ['intlRequests', 'intlRequest'] });
}

/**
 * Is the signed-in user root?
 *
 * The two overrides are root-only — narrower than `useCanFulfil`, because fulfilment ships what
 * has been paid for and does not decide who gets credit or who gets cut off. The server enforces
 * it; this decides whether to draw a button that would refuse.
 */
export function useIsRoot(): boolean {
  const roles = useAuthStore((s) => s.adminUser?.roles);
  return Boolean(roles?.some((r) => r === 'root'));
}

/**
 * The device models this lane may be used for, from the root-curated product registry.
 *
 * `cache-and-network`: the answer changes when somebody ticks a box on another page, and a rep
 * who has been told "ProMax is enabled now" should not have to hard-reload to see it. Defaults
 * to an EMPTY list while loading and on error — the safe direction for a list that decides what
 * may be ordered from an Amazon warehouse.
 */
export function useIntlMcfEligibleModels(): { models: string[]; loading: boolean } {
  const { data, loading } = useQuery<{ intlMcfEligibleModels: string[] }>(
    INTL_MCF_ELIGIBLE_MODELS,
    { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
  );
  return { models: data?.intlMcfEligibleModels ?? [], loading };
}
